/* iter-72c — Paragraph renderer with inline suggestion highlights.
 *
 * Wraps every occurrence of a pending suggestion's `anchor_text` inside
 * a paragraph with a `<mark>` element so the reader (and reviewer) can
 * see at a glance which passages have open editorial notes. Clicking a
 * highlight scrolls the SuggestionsDrawer to the matching row and
 * opens it — the parent handles that via `onAnchorClick`.
 *
 * Kept intentionally dumb: it takes a plain string + a suggestion list
 * and returns highlighted JSX. Editability wiring (`contentEditable`,
 * blur handlers, key handlers, indent style) is applied by the parent
 * exactly like it did before this refactor.
 *
 * iter55g11 — CRITICAL FIX for cut/paste "echoes", paste multiplication,
 * Grammarly undo, and browser undo history loss. Previously the editable
 * `<p>` rendered `{paragraph.text}` as a JSX text child. Every parent
 * re-render (suggestion poll tick, streaming update, chapter save) made
 * React reconcile the text child and OVERWRITE whatever the user had
 * just typed / cut / pasted / Grammarly-corrected in the DOM.
 *
 * Fix: for the editable path, render the paragraph as an UNCONTROLLED
 * DOM node — set the initial innerText via `dangerouslySetInnerHTML` on
 * mount only, and never overwrite it from React state while the writer
 * is inside it. A useEffect syncs innerText → prop when the prop changes
 * from OUTSIDE (e.g. a chapter switch or an "accept suggestion" that
 * rewrote the paragraph), guarded by focus so we never step on live
 * edits. This restores native browser undo/redo, Grammarly, cut/paste,
 * and lets multi-line paste land as-is instead of duplicating.
 */
import React, { forwardRef, useRef, useEffect, useCallback, useState } from 'react';
/* eslint-disable react-hooks/exhaustive-deps */

/* Split `text` into an array of segments alternating between plain
 * strings and highlight objects. Longest anchors are matched first so a
 * shorter anchor cannot fragment a larger one. */
function buildSegments(text, suggestions) {
  if (!text || !suggestions?.length) return [{ kind: 'text', value: text || '' }];
  const anchors = suggestions
    .filter((s) => (s?.anchor_text || '').length > 0)
    .sort((a, b) => (b.anchor_text.length - a.anchor_text.length));
  let segments = [{ kind: 'text', value: text }];
  for (const s of anchors) {
    const next = [];
    for (const seg of segments) {
      if (seg.kind !== 'text' || !seg.value.includes(s.anchor_text)) {
        next.push(seg);
        continue;
      }
      const parts = seg.value.split(s.anchor_text);
      parts.forEach((part, i) => {
        if (part) next.push({ kind: 'text', value: part });
        if (i < parts.length - 1) {
          next.push({ kind: 'hit', value: s.anchor_text, suggestion: s });
        }
      });
    }
    segments = next;
  }
  return segments;
}

// iter55g11 — Escape user text for `dangerouslySetInnerHTML` insertion.
// contentEditable will preserve `\n` inside <p> as visual line breaks
// (once white-space:pre-wrap is applied by the ManuscriptBody CSS).
// We ONLY escape the four HTML-critical characters — no other transform,
// so what comes back out via innerText round-trips 1:1.
function escapeHtml(s) {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

const HighlightedParagraph = forwardRef(function HighlightedParagraph(
  {
    paragraph,
    index,
    suggestions,
    editable,
    settings,
    firstLetterDrop,
    onBlur,
    onKeyDown,
    onInput,
    onAnchorClick,
    aiShare = null,   // iter-p3-v — 0-100 from editing-record aggregation
  },
  ref,
) {
  const segments = buildSegments(paragraph.text, suggestions);
  const style = settings?.paragraph_indent_em
    ? { textIndent: `${settings.paragraph_indent_em}em` }
    : undefined;
  // iter-p3-v — Subtle left-edge tint reflecting the chapter's AI
  // share. We don't yet track per-paragraph provenance, so every
  // paragraph carries the chapter-level tint as a light-touch
  // signal.  Colour buckets match the ChaptersPanel badge + the
  // ProvenanceWidget for consistency.
  const aiTint =
    typeof aiShare !== 'number' || aiShare <= 0
      ? ''
      : aiShare >= 66
        ? 'border-l-2 border-amber-400/60 pl-3 '
        : aiShare >= 34
          ? 'border-l-2 border-emerald-400/60 pl-3 '
          : 'border-l-2 border-sky-400/60 pl-3 ';
  const className =
    aiTint
    + (firstLetterDrop
      ? 'first-letter:text-6xl first-letter:font-semibold first-letter:float-left first-letter:mr-2 first-letter:mt-1 first-letter:leading-[0.85] first-letter:text-primary '
      : '')
    + (editable
      ? 'outline-none focus:bg-primary/5 focus:rounded-sm transition-colors cursor-text'
      : '');

  /* ------------------------------------------------------------------
   * iter55g11 — Uncontrolled editable paragraph. See file-header note.
   * iter55g11d — Sync innerText ONLY when the paragraph.text PROP
   *              changes upstream.
   * iter55g11e — Removed `dangerouslySetInnerHTML` entirely. Every
   *              time React commits a render, that prop makes the
   *              reconciler consider re-writing innerHTML — which was
   *              clobbering pastes within ~250ms of a parent state
   *              update. Now the DOM is populated exactly once (via
   *              the ref callback on first mount) and thereafter ONLY
   *              our useEffect touches it, and ONLY when the prop
   *              changes and the paragraph is unfocused.
   * ------------------------------------------------------------------ */
  const internalRef  = useRef(null);
  const lastSyncedRef = useRef(paragraph.text || '');
  const initialTextRef = useRef(paragraph.text || '');
  // Feb-2026 — Hover-card state for the read-only path. MUST be
  // declared BEFORE the `if (editable) return …` early return so the
  // hook call order stays identical across renders (react-hooks/rules-
  // of-hooks). The editable path renders a plain contentEditable <p>
  // and never reads this state; the read-only path uses it below to
  // drive the ReadOnlyHoverCard.
  const [hover, setHover] = useState(null);
  const setRefs = useCallback((el) => {
    // First-mount ONLY: seed the DOM with the paragraph's initial text.
    // React's reconciler is never allowed near the innerText/innerHTML
    // of this node again — see file-header rationale.
    if (el && !internalRef.current && editable) {
      el.textContent = initialTextRef.current;
    }
    internalRef.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) ref.current = el;
  }, [editable, ref]);

  useEffect(() => {
    if (!editable) return;
    const el = internalRef.current;
    if (!el) return;
    const desired = paragraph.text || '';
    if (desired === lastSyncedRef.current) return;
    if (document.activeElement === el) {
      lastSyncedRef.current = desired;
      return;
    }
    el.textContent = desired;
    lastSyncedRef.current = desired;
  }, [paragraph.text, editable]);

  // iter55g11c — Native (non-React) capture-phase paste listener.
  // React's synthetic `onPaste` prop was missing paste events in some
  // native Ctrl/Cmd+V flows (Playwright automation confirmed this: the
  // browser fired a real paste event on the element at capture phase
  // with the correct clipboard text, but React's onPaste bubble handler
  // never ran). Registering the listener directly on the DOM node in
  // capture phase makes the intercept unmissable.
  useEffect(() => {
    if (!editable) return undefined;
    const el = internalRef.current;
    if (!el) return undefined;

    const onPaste = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const cd = e.clipboardData || window.clipboardData;
      const text = (cd && cd.getData ? cd.getData('text/plain') : '') || '';
      if (!text) return;

      const sel = window.getSelection();
      let range;
      const inThisPara = sel && sel.rangeCount > 0 && el.contains(sel.anchorNode);
      if (inThisPara) {
        range = sel.getRangeAt(0);
      } else {
        el.focus();
        range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
      }
      range.deleteContents();
      const node = document.createTextNode(text);
      range.insertNode(node);
      range.setStartAfter(node);
      range.setEndAfter(node);
      sel.removeAllRanges();
      sel.addRange(range);
      el.dispatchEvent(new Event('input', { bubbles: true }));
    };

    el.addEventListener('paste', onPaste, true);   // capture phase
    return () => el.removeEventListener('paste', onPaste, true);
  }, [editable]);

  if (editable) {
    return (
      <p
        ref={setRefs}
        style={style}
        className={className}
        contentEditable
        suppressContentEditableWarning
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        onInput={onInput}
        // iter55g11c — Paste is intercepted via a native capture-phase
        // listener attached in useEffect above (React's onPaste was
        // missing some browser-fired events).
        // iter55g11e — NO `dangerouslySetInnerHTML` / no children — the
        // DOM text is seeded in `setRefs` (ref callback) exactly once
        // and thereafter mutated only by our own useEffect. React's
        // reconciler must not touch this node's contents again.
        data-testid={`manuscript-para-${index}`}
        spellCheck
        data-gramm="true"
        data-gramm_editor="true"
        data-enable-grammarly="true"
      />
    );
  }

  // Read-only path — unchanged (highlight rendering).
  // Feb-2026 — Rich hover card for anchored marks in the read-only
  // path. The editable path already renders a full overlay + hover
  // popover via useSuggestionOverlays; read-only viewers previously
  // only got the native browser `title` tooltip. `hover` / `setHover`
  // are declared at the top of the component (above the editable
  // early-return) to keep the hook call order stable across renders.
  return (
    <p
      ref={setRefs}
      style={style}
      className={className + ' relative'}
      data-testid={`manuscript-para-${index}`}
      data-gramm="true"
      data-gramm_editor="true"
      data-enable-grammarly="true"
    >
      {segments.map((seg, i) => {
        if (seg.kind !== 'hit') {
          return <React.Fragment key={`${paragraph.id}-t-${i}`}>{seg.value}</React.Fragment>;
        }
        const s = seg.suggestion;
        const onEnter = () => setHover(s);
        const onLeave = () => setHover((cur) => (cur && cur.id === s.id ? null : cur));
        // iter55g5 — Inline "diff" preview for anchored EDIT suggestions.
        if (s.kind === 'edit' && s.proposed_text) {
          return (
            <span
              key={`${paragraph.id}-h-${i}`}
              data-testid={`manuscript-anchor-${s.id}`}
              data-suggestion-id={s.id}
              title={`Suggested edit by ${s.author_name}`}
              onClick={(e) => onAnchorClick?.(s, e)}
              onMouseEnter={onEnter}
              onMouseLeave={onLeave}
              className="group cursor-pointer rounded-sm transition-colors hover:ring-1 hover:ring-amber-400/60"
            >
              <span
                data-testid={`manuscript-anchor-strike-${s.id}`}
                className="bg-rose-100/80 px-0.5 text-rose-900 line-through decoration-rose-400 decoration-1 dark:bg-rose-500/25 dark:text-rose-100"
              >
                {seg.value}
              </span>
              <span className="mx-0.5 text-[13px] font-semibold text-muted-foreground group-hover:text-primary">
                &rarr;
              </span>
              <span
                data-testid={`manuscript-anchor-proposed-${s.id}`}
                className="bg-emerald-100/80 px-0.5 text-emerald-900 dark:bg-emerald-500/25 dark:text-emerald-100"
              >
                {s.proposed_text}
              </span>
            </span>
          );
        }
        // COMMENT (or edit-without-proposal) — original amber/sky highlight.
        return (
          <mark
            key={`${paragraph.id}-h-${i}`}
            data-testid={`manuscript-anchor-${s.id}`}
            data-suggestion-id={s.id}
            title={
              s.kind === 'edit'
                ? `Suggested edit by ${s.author_name}`
                : `Note by ${s.author_name}`
            }
            onClick={(e) => onAnchorClick?.(s, e)}
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
            className={
              'cursor-pointer rounded-sm px-0.5 transition-colors '
              + (s.kind === 'edit'
                ? 'bg-amber-200/70 hover:bg-amber-300 dark:bg-amber-500/30 dark:hover:bg-amber-500/50'
                : 'bg-sky-200/70 hover:bg-sky-300 dark:bg-sky-500/30 dark:hover:bg-sky-500/50')
            }
          >
            {seg.value}
          </mark>
        );
      })}
      {hover && (
        <ReadOnlyHoverCard suggestion={hover} />
      )}
    </p>
  );
});

/* Feb-2026 — Rich hover card mirroring the editable-path popover.
 * Positioned inline (absolute, below the mark's line) via CSS so we
 * don't have to duplicate the pixel-math from useSuggestionOverlays.
 * pointer-events-none so it never blocks the click on the underlying
 * mark. Kept out of the main component body to keep it under 300 LOC. */
function ReadOnlyHoverCard({ suggestion: s }) {
  const isEdit = s.kind === 'edit';
  return (
    <span
      data-testid={`manuscript-hover-card-${s.id}`}
      role="tooltip"
      className={
        'pointer-events-none absolute left-0 top-full z-20 mt-1 block max-w-[340px] rounded-md border p-2.5 text-[13.5px] shadow-lg backdrop-blur-sm ' +
        (isEdit
          ? 'border-amber-500/60 bg-amber-50/95 text-amber-950 dark:bg-amber-900/80 dark:text-amber-50'
          : 'border-sky-500/60 bg-sky-50/95 text-sky-950 dark:bg-sky-900/80 dark:text-sky-50')
      }
    >
      <span className="mb-1 flex items-center gap-1.5 text-[9.5px] font-semibold uppercase tracking-wider opacity-80">
        {s.author_picture ? (
          <img
            src={s.author_picture}
            alt=""
            className="h-4 w-4 rounded-full border border-current/30 object-cover"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <span className={
            'inline-flex h-4 w-4 items-center justify-center rounded-full text-[8.5px] font-bold ' +
            (isEdit ? 'bg-amber-500 text-white' : 'bg-sky-500 text-white')
          }>
            {(s.author_name || 'A').charAt(0).toUpperCase()}
          </span>
        )}
        <span>{isEdit ? 'Suggested edit' : 'Comment'}</span>
        <span>·</span>
        <span>{s.author_name || 'reviewer'}</span>
      </span>
      {isEdit && s.proposed_text ? (
        <span className="block space-y-1">
          <span className="block line-through opacity-60">
            &ldquo;{(s.anchor_text || '').slice(0, 180)}&rdquo;
          </span>
          <span className="block font-medium">
            &rarr; &ldquo;{(s.proposed_text || '').slice(0, 260)}&rdquo;
          </span>
          {s.note && (
            <span className="mt-1 block border-t border-current/20 pt-1 text-[13px] italic opacity-90">
              {s.note}
            </span>
          )}
        </span>
      ) : (
        <span className="block whitespace-pre-wrap">
          {(s.note || '(no comment text)').slice(0, 500)}
        </span>
      )}
      <span className="mt-1.5 block text-[9.5px] opacity-60">
        Click to open in the Suggestions drawer
      </span>
    </span>
  );
}

export default HighlightedParagraph;
